/**
 * SentenceMenu — §5.6. The feature Karti asked for by name.
 *
 * His words: *"select and regenerate specific sentences by right click context menu and regenerate
 * voice"*. Everything here addresses a **sid**, and nothing here addresses a pixel, a second, a page
 * number or a file boundary — which is why regenerating a sentence cannot move your place, and why
 * the same menu works identically on a phone whose columns bear no relation to the audio's pages.
 *
 * ── Desktop: `contextmenu`, and the escape that costs one condition ─────────
 *
 * `preventDefault()` on `[data-s]` — **except `e.shiftKey`, which passes through to the browser's own
 * menu**. A reading app that permanently steals right-click and offers no way out is user-hostile:
 * "Inspect", "Look Up", "Add to dictionary" and "Search with…" are things people legitimately do to a
 * word in a book, and the escape is one boolean.
 *
 * ── The SELECTION case, which is the one he described ──────────────────────
 *
 * "Right-click on a **selected** sentence." No subsystem design handled a selection, and it is the
 * common case for a mangled name that runs across a clause: you drag over the sentences that sound
 * wrong and fix them together. If a selection exists, the menu targets **every `[data-s]` it
 * intersects** and reads *"Regenerate 3 sentences"*. One request, one page re-stitch, one job.
 *
 * ── iOS: 450 ms, 10 px, 90 ms, and no haptic ───────────────────────────────
 *
 * The native long-press callout and a custom long-press menu are mutually exclusive (see
 * `SelectionMode`), so reading mode suppresses the callout and this file provides the hold. 450 ms
 * with a 10 px slop — below that a slow page-turn swipe opens a menu; above it the gesture feels
 * broken. The acknowledgement is a **90 ms scale pulse and nothing else**: `navigator.vibrate` does
 * not exist in iOS Safari, and shipping a haptic that only fires on Android is not "done", it is a
 * feature that works on the device this product is not primarily for.
 *
 * ── LAW 13 ──────────────────────────────────────────────────────────────────
 *
 * **Every row is computed from `shared/access.ts`**, never from a build flag, never from a tier string
 * this file inferred, never from a `disabled` prop somebody hardcoded. `AB_ANON_REGEN` does not exist
 * and must not be added. §0 decision 1 — anonymous readers cannot regenerate — was put to Karti and
 * confirmed on 2026-08-06; do not re-litigate it. What anon keeps is **"Sounds wrong"**, in the same
 * place on the same menu, writing the identical `regen_events` row with `applied: false`. Zero GPU,
 * full signal, and it is never rate-limited.
 *
 * The server decides again, authoritatively, in `server/src/routes/sentences.ts`. This file's answer
 * is a courtesy — it is what stops a reader being offered a button that will refuse them — and the two
 * cannot drift because they call the same function.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import { canEdit, canRegenerate, type AccessBook } from "../../../shared/access.ts";
import type { MaybeActor } from "../../../shared/actor.ts";
import { compareAddress } from "../../../shared/address.ts";
import { ApiError, api } from "../lib/api.ts";
import { reportOrQueue } from "../lib/outbox.ts";
import { SelectionChip, injectReaderMenuCss, sidAt, sidsInSelection, type SelectionModeState } from "./SelectionMode.tsx";
import { TakePicker, sentenceApi, type TakesResponse } from "./TakePicker.tsx";
import { PronunciationSheet, saved } from "./PronunciationSheet.tsx";
import { ReactionPanel } from "./PersistentReactions.tsx";

// ─── the target ──────────────────────────────────────────────────────────────

export interface SentenceMenuTarget {
  /** Every sentence this menu acts on, in reading order. One element unless a selection was live. */
  sids: string[];
  /** The one the pointer was actually on — what "Play from here" and "Start the book here" use. */
  anchorSid: string;
  /** Viewport coordinates. The menu flips rather than overflowing. */
  x: number;
  y: number;
  /** True when the sid set came from a text selection rather than from the element under the pointer. */
  fromSelection: boolean;
}

/**
 * The word under the reader's finger, for the lexicon sheet's prefill.
 *
 * Read from the live selection rather than from `target`, because `SentenceMenuTarget` addresses
 * SENTENCES (LAW 1's coordinate system) and a lexicon entry is about a word. An empty string is a
 * perfectly good answer — the sheet's field is editable and a wrong prefill is worse than none,
 * since the pattern is what decides how many sentences get re-narrated.
 */
function selectedWord(): string {
  if (typeof window === "undefined") return "";
  const raw = (window.getSelection?.()?.toString() ?? "").trim();
  // One word. A dragged phrase is a `pattern` that would match almost nothing and quote "0 sentences".
  return /^[^\s]{1,64}$/u.test(raw) ? raw : "";
}

/** §5.6. Below this a slow swipe opens a menu; above it the gesture reads as broken. */
export const LONG_PRESS_MS = 450;
/** A finger that has travelled this far is scrolling or turning a page, not holding. */
export const LONG_PRESS_SLOP_PX = 10;
/** The acknowledgement. No haptic — see the file header. */
export const PRESS_PULSE_MS = 90;

// ─── the opener ──────────────────────────────────────────────────────────────

export interface UseSentenceMenu {
  target: SentenceMenuTarget | null;
  /** Open it on a sentence without a gesture — the keyboard path, and the selection chip's. */
  openOn(sids: string[], at: { x: number; y: number }, fromSelection?: boolean): void;
  close(): void;
}

/**
 * Attach the two openers to the reader's track.
 *
 * ⚠️ Listeners go on the **track**, not on each `[data-s]` span. A chapter is thousands of them; one
 * delegated listener is one registration and it keeps working when a repagination replaces the DOM,
 * which happens on every font-size change and every rotation. Per-span listeners would have to be
 * re-attached by whichever code happened to rebuild the page, and the failure — a menu that stops
 * opening after you change the type size — is exactly the kind nobody reports as a bug.
 */
export function useSentenceMenu(o: { track: HTMLElement | null; enabled?: boolean; selectionMode?: SelectionModeState | null }): UseSentenceMenu {
  const { track, enabled = true, selectionMode = null } = o;
  const [target, setTarget] = useState<SentenceMenuTarget | null>(null);

  const openOn = useCallback((sids: string[], at: { x: number; y: number }, fromSelection = false) => {
    const ordered = [...new Set(sids)].sort(compareAddress);
    if (ordered.length === 0) return;
    setTarget({ sids: ordered, anchorSid: ordered[0]!, x: at.x, y: at.y, fromSelection });
  }, []);

  const close = useCallback(() => setTarget(null), []);

  useEffect(() => injectReaderMenuCss(), []);

  // ── desktop ──
  useEffect(() => {
    if (track === null || !enabled) return;

    const onContextMenu = (e: MouseEvent) => {
      // THE ESCAPE. Shift-right-click is the browser's menu, always, on every element. One condition,
      // and it is the difference between a reader and a hostage situation.
      if (e.shiftKey) return;

      const under = sidAt(e.target);
      if (under === null) return;

      // A live selection wins over the element under the pointer: "right-click on a SELECTED sentence"
      // means the selection is the subject. Only when the click landed inside it, though — right-
      // clicking somewhere else with an old selection still live must act on what you pointed at.
      const selected = sidsInSelection(track);
      const useSelection = selected.length > 0 && selected.includes(under);

      e.preventDefault();
      const sids = useSelection ? selected : [under];
      const ordered = [...sids].sort(compareAddress);
      setTarget({ sids: ordered, anchorSid: under, x: e.clientX, y: e.clientY, fromSelection: useSelection });
    };

    track.addEventListener("contextmenu", onContextMenu);
    return () => track.removeEventListener("contextmenu", onContextMenu);
  }, [track, enabled]);

  // ── touch ──
  useEffect(() => {
    if (track === null || !enabled) return;

    let timer = 0;
    let startX = 0;
    let startY = 0;
    let pressed: HTMLElement | null = null;

    const clear = () => {
      if (timer !== 0) window.clearTimeout(timer);
      timer = 0;
      pressed?.removeAttribute("data-ab-press");
      pressed = null;
    };

    const onDown = (e: PointerEvent) => {
      // Fine pointers get `contextmenu` and must not also get a hold-to-open: a mouse button held
      // still for half a second while reading is not a request for a menu.
      if (e.pointerType === "mouse") return;
      // While native selection is restored, iOS owns the long press. Racing its callout is the exact
      // thing §5.6 says cannot be done.
      if (selectionMode?.active === true) return;

      const el = e.target instanceof Element ? e.target.closest<HTMLElement>("[data-s]") : null;
      const sid = el?.getAttribute("data-s") ?? null;
      if (el === null || sid === null || sid === "") return;

      startX = e.clientX;
      startY = e.clientY;
      pressed = el;
      timer = window.setTimeout(() => {
        // The pulse first, then the menu one frame later, so the acknowledgement is visible rather
        // than instantly covered by the panel that it is acknowledging.
        el.setAttribute("data-ab-press", "1");
        window.setTimeout(() => {
          el.removeAttribute("data-ab-press");
          setTarget({ sids: [sid], anchorSid: sid, x: startX, y: startY, fromSelection: false });
        }, PRESS_PULSE_MS);
        timer = 0;
        pressed = null;
      }, LONG_PRESS_MS);
    };

    const onMove = (e: PointerEvent) => {
      if (timer === 0) return;
      if (Math.abs(e.clientX - startX) > LONG_PRESS_SLOP_PX || Math.abs(e.clientY - startY) > LONG_PRESS_SLOP_PX) clear();
    };

    track.addEventListener("pointerdown", onDown);
    track.addEventListener("pointermove", onMove);
    track.addEventListener("pointerup", clear);
    track.addEventListener("pointercancel", clear);
    // A scroll or a page turn started elsewhere still ends the hold: the finger is doing something
    // else now, and a menu that appears after the page has moved points at the wrong sentence.
    window.addEventListener("scroll", clear, true);
    return () => {
      clear();
      track.removeEventListener("pointerdown", onDown);
      track.removeEventListener("pointermove", onMove);
      track.removeEventListener("pointerup", clear);
      track.removeEventListener("pointercancel", clear);
      window.removeEventListener("scroll", clear, true);
    };
  }, [track, enabled, selectionMode]);

  return { target, openOn, close };
}

// ─── the reasons ─────────────────────────────────────────────────────────────

/**
 * §4.12's labels. They are the *reason* column of `regen_events`, and the analysis that matters ranks
 * `text_changed = 1` rows by token pair — so these exist to make the third ranking possible: rows
 * where the text did NOT change, clustered by what the human said was wrong with the reading. That is
 * the cluster that identifies **model** defects no lexicon fixes.
 *
 * A "Regenerate" that filed everything as `other` would be a button with a bill and no evidence, which
 * is why picking one IS the button rather than a step before it.
 */
const REASONS: readonly { id: string; label: string }[] = [
  { id: "mispronounced", label: "Mispronounced" },
  { id: "wrong-emphasis", label: "Wrong emphasis" },
  { id: "too-fast", label: "Too fast" },
  { id: "glitch", label: "Glitch or noise" },
  { id: "cut-off", label: "Cut off" },
  { id: "other", label: "Something else" },
];

// ─── the menu ────────────────────────────────────────────────────────────────

export interface SentenceMenuProps {
  target: SentenceMenuTarget | null;
  /**
   * The book, structurally — `ownerId`, `visibility`, `deletedAt`, `takenDownAt`. `BookMeta` satisfies
   * it without a mapping step, which is deliberate: a mapping step is where a `takenDownAt` gets
   * dropped and a taken-down book grows an edit menu.
   */
  book: (AccessBook & { id: string; currentEditionId?: string | null }) | null;
  actor: MaybeActor;
  voiceId?: string | null;
  /** Co-read context: names come from this room, and a saved reaction also uses its instant float. */
  roomCode?: string | null;
  onRoomReaction?: ((emoji: string, sid: string) => void) | null;
  onClose(): void;

  /** LAW 12's explicit seek. Turning a page is browsing; THIS is how the narration moves. */
  onPlayFrom(sid: string): void;
  /** Pause before an audition — a phone does not play two things at once. */
  onPauseReader?: (() => void) | null;
  /** Enter §5.6's selection mode. Absent on a fine pointer, where native selection already works. */
  onSelectText?: (() => void) | null;
  /**
   * Bookmark and Note are `annotations` rows, and that route is not built yet. They are **props rather
   * than a disabled row** so the menu shows them exactly when the host can honour them: the capability
   * is the presence of a handler, which cannot rot the way a hardcoded `disabled` does.
   */
  onBookmark?: ((sid: string) => void) | null;
  onNote?: ((sid: string) => void) | null;
  /** §4.11's Retry, offered only when this page's narration actually failed. */
  onRetry?: (() => void) | null;
  failed?: boolean;
  /** A one-line message for the reader's existing notice line. */
  onNotice?: ((message: string) => void) | null;
  /**
   * Something changed on the server that affects this page's audio. The host re-fetches the page's
   * narration and **restores its position by `sid`** — never by page or by time (§4.11).
   */
  onChanged?: ((r: { sids: string[]; jobs: { id: string; pageId: string; status: string }[] }) => void) | null;
}

type Panel =
  | { k: "menu" }
  | { k: "reason"; apply: boolean; sids: string[] }
  | { k: "pronounce" }
  | { k: "lexicon"; word: string }
  | { k: "takes" }
  | { k: "reaction" };

export function SentenceMenu(props: SentenceMenuProps): ReactElement | null {
  const { target, book, actor, voiceId = null, roomCode = null, onRoomReaction = null, onClose, onPlayFrom, onPauseReader, onSelectText, onBookmark, onNote, onRetry, failed = false, onNotice, onChanged } = props;

  const [panel, setPanel] = useState<Panel>({ k: "menu" });
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<TakesResponse | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  const sids = target?.sids ?? [];
  const n = sids.length;
  const mayRegenerate = book !== null && canRegenerate(book, actor);
  const mayEditStructure = book !== null && canEdit(book, actor);

  // Reset to the top of the menu whenever it opens on something new. Without this, opening the menu
  // on a second sentence lands you in the pronunciation editor for the first one.
  useEffect(() => {
    setPanel({ k: "menu" });
    setMeta(null);
  }, [target?.anchorSid, target?.sids.length]);

  useEffect(() => injectReaderMenuCss(), []);

  /**
   * The metadata the rows need — the current spoken text, and whether per-sentence tuning would
   * survive the planner.
   *
   * ⚠️ `tuningApplies` is computed on the SERVER by comparing the key it would store against the key
   * the planner will recompute. It is not a phase constant and there is nothing here to forget to flip.
   * AGENTS.md records exactly what a hardcoded one costs: books shipped a button badged *"Needs voice
   * capture — Phase 6"* that stayed bolted shut against a feature which had already landed in Phase 6.
   */
  useEffect(() => {
    if (target === null || book === null || !mayRegenerate) return;
    let live = true;
    void sentenceApi
      .takes(target.anchorSid, book.id, voiceId)
      .then((r) => {
        if (live) setMeta(r);
      })
      .catch(() => {
        /* The menu still works without it; only the two rows that need it stay unavailable. */
      });
    return () => {
      live = false;
    };
  }, [target, book, voiceId, mayRegenerate]);

  // Focus management and Escape, the same shape `ChapterDrawer` uses — and for the same reason: the
  // reader's arrow keys turn pages, and a menu that lets them through flips the book behind itself.
  useEffect(() => {
    if (target === null) return;
    restoreFocusTo.current = document.activeElement;
    boxRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Swallowed rather than handled: while a menu is up, an arrow key must not turn the page it
        // is anchored to. Roving focus is Tab's job and the browser already does it correctly.
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [target, onClose]);

  // Position, after paint, so the box is measured rather than guessed. A menu opened near the bottom
  // of a phone flips above the finger instead of hanging off the screen.
  useEffect(() => {
    const box = boxRef.current;
    if (box === null || target === null) return;
    const rect = box.getBoundingClientRect();
    const pad = 8;
    const x = Math.max(pad, Math.min(target.x, window.innerWidth - rect.width - pad));
    const y = target.y + rect.height + pad > window.innerHeight ? Math.max(pad, target.y - rect.height) : target.y;
    box.style.left = `${x}px`;
    box.style.top = `${y}px`;
  }, [target, panel, meta]);

  const notice = useCallback((m: string) => onNotice?.(m), [onNotice]);

  // ── the two writes ──

  const submitReason = useCallback(
    async (reason: string, note: string, apply: boolean, on: string[]) => {
      if (book === null || on.length === 0) return;
      setBusy(true);
      try {
        if (apply) {
          const r = await sentenceApi.regenerate(on[0]!, {
            bookId: book.id,
            reason,
            note: note === "" ? undefined : note,
            with: on.slice(1),
            voiceId,
          });
          onChanged?.({ sids: on, jobs: r.jobs.map((j) => ({ id: j.id, pageId: j.pageId, status: j.status })) });
          notice(
            on.length === 1
              ? "Re-reading that sentence — it takes a few seconds, and your place does not move."
              : `Re-reading ${on.length} sentences — your place does not move.`,
          );
        } else {
          // "Sounds wrong", for everyone including anonymous readers. One row per sentence, because
          // the hotspots view ranks by sentence and a single row naming five would be unrankable.
          //
          // ⚠️ P6, §5.8: it goes through the OUTBOX, which sends it now and queues it in IndexedDB
          // if the network refuses. §4.12 makes this the one telemetry path an anonymous reader
          // has — the primary intake for the signal the whole lexicon is built from — and losing it
          // because a tunnel arrived first is losing exactly the reports from the person listening
          // on a train. The return value is what actually happened, so the notice below is not the
          // small lie ("Reported — thanks") that teaches people their reports do nothing.
          let queued = false;
          for (const sid of on) {
            const sent = await reportOrQueue(sid, { bookId: book.id, reason, note: note === "" ? undefined : note });
            if (!sent) queued = true;
          }
          notice(queued ? "Saved — it goes to Karti when you're back online." : "Reported — thanks.");
        }
        onClose();
      } catch (err) {
        notice(err instanceof ApiError ? err.message : "That did not go through. Nothing was changed.");
      } finally {
        setBusy(false);
      }
    },
    [book, voiceId, onChanged, onClose, notice],
  );

  const setSpeak = useCallback(
    async (speak: boolean) => {
      if (book === null || target === null) return;
      setBusy(true);
      try {
        await sentenceApi.speak(target.anchorSid, { bookId: book.id, speak, voiceId });
        onChanged?.({ sids: [target.anchorSid], jobs: [] });
        notice(speak ? "This sentence will be read aloud again." : "This sentence will be skipped when the book is read aloud.");
        onClose();
      } catch (err) {
        notice(err instanceof ApiError ? err.message : "Could not change that.");
      } finally {
        setBusy(false);
      }
    },
    [book, target, voiceId, onChanged, onClose, notice],
  );

  const startBookHere = useCallback(async () => {
    if (book === null || target === null) return;
    const editionId = meta?.editionId ?? book.currentEditionId ?? null;
    if (editionId === null) {
      notice("This book is still being imported.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/editions/${encodeURIComponent(editionId)}/confirm`, { bodyStartSid: target.anchorSid });
      notice("The book now starts here. The first three pages are being narrated.");
      onClose();
    } catch (err) {
      notice(err instanceof ApiError ? err.message : "Could not move the start of the book.");
    } finally {
      setBusy(false);
    }
  }, [book, target, meta, onClose, notice]);

  const copy = useCallback(() => {
    const text = meta?.text ?? "";
    if (text === "") return;
    void navigator.clipboard?.writeText(text).then(
      () => notice("Copied."),
      () => notice("The browser would not let us copy that."),
    );
    onClose();
  }, [meta, onClose, notice]);

  const share = useCallback(() => {
    if (target === null) return;
    // `#s=<sid>` — §5.6. The sid IS the address, so the link survives a re-pagination, a font-size
    // change and a device swap; a `?page=` would not survive any of the three.
    const url = `${window.location.origin}${window.location.pathname}#s=${encodeURIComponent(target.anchorSid)}`;
    void navigator.clipboard?.writeText(url).then(
      () => notice("Link to this passage copied."),
      () => notice("The browser would not let us copy that."),
    );
    onClose();
  }, [target, onClose, notice]);

  const rows = useMemo(() => {
    if (target === null) return null;

    const item = (key: string, label: string, onClick: () => void, opts: { hint?: string; disabled?: boolean; danger?: boolean } = {}) => (
      <button
        key={key}
        type="button"
        className="ab-sm-item"
        role="menuitem"
        data-danger={opts.danger ? "1" : undefined}
        disabled={opts.disabled === true || busy}
        onClick={onClick}
      >
        <span>{label}</span>
        {opts.hint === undefined ? null : <span className="ab-sm-hint">{opts.hint}</span>}
      </button>
    );

    const out: ReactElement[] = [];

    out.push(item("play", "Play from here", () => {
      onPlayFrom(target.anchorSid);
      onClose();
    }));

    if (mayRegenerate) {
      out.push(item("regen", n === 1 ? "Regenerate voice" : `Regenerate ${n} sentences`, () => setPanel({ k: "reason", apply: true, sids })));
      out.push(item("takes", "Other takes…", () => setPanel({ k: "takes" }), { disabled: n !== 1 }));
      out.push(
        item("pronounce", "Fix pronunciation…", () => setPanel({ k: "pronounce" }), {
          disabled: n !== 1,
          hint: n === 1 ? undefined : "one at a time",
        }),
      );
      /**
       * §4.3's lexicon, as distinct from the row above it — and it is the row §12's P3 ship list
       * calls *"the pronunciation sheet with metered Preview"*.
       *
       * ⚠️ It was written, finished and imported by **nothing**: 618 lines with a working
       * `previewing` state, a real `audio/wav` fetch and an `<audio>` element carrying the iOS
       * gesture note, and `grep -rn PronunciationSheet client/src` outside its own file returned
       * nothing. What shipped instead was `PronouncePanel` below — a text box and a *Say it this
       * way* button with **no way to hear it before committing GPU**. Production had `pronunciations`
       * 0 rows and `regen_events` 0 rows, which is what "nobody could reach it" looks like from the
       * database.
       *
       * The two are not the same feature and neither replaces the other: *Fix pronunciation* rewrites
       * THIS sentence (`sentence_overrides`, keyed by anchor), and this one teaches the book — or the
       * whole library, if a god says so — how a word is said (`pronunciations`, §4.3), pricing the
       * blast radius before it spends anything.
       */
      out.push(
        item("lexicon", "Always say it this way…", () => setPanel({ k: "lexicon", word: selectedWord() }), {
          hint: "for the whole book",
        }),
      );
      out.push(
        item("tune", "Tune…", () => undefined, {
          disabled: true,
          /**
           * An honest stub, and the condition it is bolted to is REAL: `tuningApplies` comes back
           * from the server, which computed it by comparing the key it would store against the key
           * the planner will recompute. The day `RenderSpec` carries a per-sentence parameter, this
           * row turns itself on. Until then the hint says what is missing rather than what phase it
           * is in — a phase number is a claim that goes stale in the direction nobody checks.
           */
          hint: meta?.tuningApplies === true ? undefined : "needs per-sentence settings in the planner",
        }),
      );
    }

    // Persistent and social, but still anchored to the exact sentence/block rather than a screen
    // coordinate. This opens a panel instead of reacting immediately so keyboard and screen-reader
    // users get the same choice and can see who has already reacted.
    out.push(item("reaction", "React…", () => setPanel({ k: "reaction" }), { hint: "sentence or paragraph" }));

    // "Sounds wrong" — EVERYONE, always, zero GPU, never rate-limited. Same place on the menu whether
    // you are Karti or a stranger on a public book; the row that changes is the one above it.
    out.push(item("report", "Sounds wrong", () => setPanel({ k: "reason", apply: false, sids })));

    if (failed && onRetry !== null && onRetry !== undefined) {
      out.push(item("retry", "Retry this page", () => {
        onRetry();
        onClose();
      }, { hint: "only what's missing" }));
    }

    out.push(<hr key="sep1" className="ab-sm-sep" />);

    if (onBookmark) out.push(item("bookmark", "Bookmark", () => {
      onBookmark(target.anchorSid);
      onClose();
    }));
    if (onNote) out.push(item("note", "Note…", () => {
      onNote(target.anchorSid);
      onClose();
    }));
    out.push(item("copy", "Copy", copy, { disabled: meta === null }));
    out.push(item("share", "Share this passage", share));
    if (onSelectText) out.push(item("select", "Select text", () => {
      onSelectText();
      onClose();
    }, { hint: "30s" }));

    if (mayEditStructure) {
      out.push(<hr key="sep2" className="ab-sm-sep" />);
      out.push(item("start", "Start the book here", () => void startBookHere(), { hint: "skips front matter" }));
    }
    if (mayRegenerate) {
      out.push(
        item(
          "speak",
          meta?.speak === false ? "Read this sentence again" : "Don’t read this sentence",
          () => void setSpeak(meta?.speak === false),
          { danger: meta?.speak !== false, disabled: n !== 1 },
        ),
      );
    }

    return out;
  }, [target, sids, n, busy, meta, mayRegenerate, mayEditStructure, failed, onPlayFrom, onClose, onBookmark, onNote, onSelectText, onRetry, copy, share, startBookHere, setSpeak]);

  if (target === null || book === null) return null;

  return (
    <>
      {/* A transparent scrim, not a backdrop: it closes the menu on any outside press, including a
          page-turn swipe, without dimming a page somebody is reading. */}
      <div className="ab-sm-scrim" onPointerDown={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} aria-hidden="true" />
      <div className="ab-sm" role="menu" aria-label="Sentence" tabIndex={-1} ref={boxRef}>
        {panel.k === "menu" ? (
          <>
            {target.fromSelection ? (
              <div className="ab-sm-title-row">
                {n} sentence{n === 1 ? "" : "s"} selected
              </div>
            ) : null}
            {rows}
          </>
        ) : null}

        {panel.k === "reason" ? (
          <ReasonPanel
            apply={panel.apply}
            count={panel.sids.length}
            busy={busy}
            onCancel={() => setPanel({ k: "menu" })}
            onSubmit={(reason, note) => void submitReason(reason, note, panel.apply, panel.sids)}
          />
        ) : null}

        {panel.k === "pronounce" ? (
          <PronouncePanel
            meta={meta}
            busy={busy}
            onCancel={() => setPanel({ k: "menu" })}
            onSubmit={async (ttsText, note) => {
              if (book === null) return;
              setBusy(true);
              try {
                const r = await sentenceApi.regenerate(target.anchorSid, {
                  bookId: book.id,
                  reason: "mispronounced",
                  note: note === "" ? undefined : note,
                  ttsTextOverride: ttsText,
                  voiceId,
                });
                onChanged?.({ sids: [target.anchorSid], jobs: r.jobs.map((j) => ({ id: j.id, pageId: j.pageId, status: j.status })) });
                notice("Saying it that way from now on — re-reading the sentence now.");
                onClose();
              } catch (err) {
                notice(err instanceof ApiError ? err.message : "That did not go through. Nothing was changed.");
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : null}

        {panel.k === "lexicon" ? (
          <PronunciationSheet
            open
            bookId={book.id}
            actor={actor ?? null}
            initialPattern={panel.word}
            /**
             * `null`, deliberately. The sheet uses `pageId` to re-narrate THE page the reader is
             * standing on at priority 0, and this menu does not know it: `SentenceMenuTarget` carries
             * sids and viewport coordinates, and `TakesResponse` carries the edition and the sentence.
             * Passing a page id we do not have would be a guess arriving at the one call that spends
             * GPU. Without it the write still lands and the pages re-narrate the ordinary way, when
             * somebody opens them — a slower fix, never a wrong one.
             */
            pageId={null}
            editionId={meta?.editionId ?? book.currentEditionId ?? null}
            voiceId={voiceId}
            onClose={() => setPanel({ k: "menu" })}
            onApplied={(result) => {
              notice(saved(result));
              // No page id to attribute the job to (see `pageId` above), so the host is told the sentence
              // changed and refetches. `sids` is what §4.11 says the position is restored by anyway.
              onChanged?.({ sids: [target.anchorSid], jobs: [] });
              onClose();
            }}
          />
        ) : null}

        {panel.k === "takes" ? (
          <TakePicker
            sid={target.anchorSid}
            bookId={book.id}
            voiceId={voiceId}
            onPauseReader={onPauseReader ?? null}
            onClose={() => setPanel({ k: "menu" })}
            onActivated={(r) => onChanged?.({ sids: [r.sid], jobs: [] })}
            onNotice={onNotice ?? null}
          />
        ) : null}

        {panel.k === "reaction" ? (
          book.currentEditionId ? (
            <ReactionPanel
              bookId={book.id}
              editionId={book.currentEditionId}
              anchorSid={target.anchorSid}
              roomCode={roomCode}
              busy={busy}
              onBusy={setBusy}
              onBack={() => setPanel({ k: "menu" })}
              onNotice={onNotice ?? undefined}
              onRoomReaction={onRoomReaction ?? undefined}
            />
          ) : (
            <div className="ab-sm-panel">
              <p className="ab-sm-note">This book is still being imported, so reactions cannot be anchored yet.</p>
              <div className="ab-sm-row">
                <button type="button" className="ab-sm-cancel" onClick={() => setPanel({ k: "menu" })}>Back</button>
              </div>
            </div>
          )
        ) : null}
      </div>
    </>
  );
}

// ─── the reason panel ────────────────────────────────────────────────────────

/**
 * Picking the reason IS the button.
 *
 * A "Regenerate" that filed every row as `other` would be a GPU-spending button with no evidence
 * attached, and §4.12's entire claim is that a regeneration is a **labelled training example**. Two
 * taps: open the menu, tap what is wrong. The note is optional and is what makes the token-pair
 * hotspots view readable months later.
 */
function ReasonPanel(props: {
  apply: boolean;
  count: number;
  busy: boolean;
  onCancel(): void;
  onSubmit(reason: string, note: string): void;
}): ReactElement {
  const { apply, count, busy, onCancel, onSubmit } = props;
  const [note, setNote] = useState("");

  return (
    <div className="ab-sm-panel">
      <div className="ab-sm-head">
        <span className="ab-sm-title">
          {apply ? (count === 1 ? "What is wrong with it?" : `What is wrong with these ${count}?`) : "What sounded wrong?"}
        </span>
      </div>
      <p className="ab-sm-note ab-sm-quiet">
        {apply
          ? "Karti’s voice re-reads it. Your place does not move, and you can put the old reading back with no GPU at all."
          : "Recorded, and nothing is regenerated. This is what tells us which sentences the voice keeps getting wrong."}
      </p>
      <input
        className="ab-sm-input"
        type="text"
        value={note}
        maxLength={500}
        placeholder="Optional — e.g. “Siobhan is Shiv-awn”"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="ab-sm-row">
        {REASONS.map((r) => (
          <button key={r.id} type="button" className="ab-sm-cancel" disabled={busy} onClick={() => onSubmit(r.id, note.trim())}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="ab-sm-row">
        <button type="button" className="ab-sm-cancel" disabled={busy} onClick={onCancel}>
          Back
        </button>
      </div>
    </div>
  );
}

// ─── the pronunciation panel ─────────────────────────────────────────────────

/**
 * **The only pronunciation channel that exists.**
 *
 * Chatterbox has no SSML, no phonemes and no prosody markup — verified against the live API. So every
 * pronunciation fix is a **text** transformation: you rewrite what is *spoken* while what is *printed*
 * stays exactly as the author wrote it. That is why this panel shows both lines, and why the one you
 * edit is labelled "spoken as".
 *
 * The override is stored against `(bookId, anchor)`, so it survives a re-import through §3.6's ladder:
 * teaching the machine how to say a name is expensive human attention and losing it to a parser
 * upgrade is not acceptable.
 */
function PronouncePanel(props: {
  meta: TakesResponse | null;
  busy: boolean;
  onCancel(): void;
  onSubmit(ttsText: string, note: string): void | Promise<void>;
}): ReactElement {
  const { meta, busy, onCancel, onSubmit } = props;
  const [value, setValue] = useState<string | null>(null);
  const text = value ?? meta?.ttsText ?? "";

  if (meta === null) {
    return (
      <div className="ab-sm-panel">
        <p className="ab-sm-note">Loading this sentence…</p>
      </div>
    );
  }

  const changed = text.trim() !== "" && text !== meta.ttsText;

  return (
    <div className="ab-sm-panel">
      <div className="ab-sm-head">
        <span className="ab-sm-title">Fix pronunciation</span>
      </div>
      <p className="ab-sm-quote">{meta.text}</p>
      <p className="ab-sm-note ab-sm-quiet">
        Spoken as — spell it how it should sound. The printed text above never changes.
      </p>
      <input className="ab-sm-input" type="text" value={text} onChange={(e) => setValue(e.target.value)} spellCheck={false} />
      <div className="ab-sm-row">
        {meta.ttsText === meta.ttsTextDefault ? null : (
          <button type="button" className="ab-sm-cancel" disabled={busy} onClick={() => setValue(meta.ttsTextDefault)}>
            Reset
          </button>
        )}
        <button type="button" className="ab-sm-cancel" disabled={busy} onClick={onCancel}>
          Back
        </button>
        <button type="button" className="ab-sm-go" disabled={busy || !changed} onClick={() => void onSubmit(text.trim(), "")}>
          Say it this way
        </button>
      </div>
    </div>
  );
}

export { SelectionChip };
export default SentenceMenu;
